import type { Role } from '@mm-shared/types/MilestoneMap1';

/**
 * Permission flags that can be derived from a user's role
 */
export interface RolePermissions {
  // 1. Viewer permissions
  canViewMap: boolean;
  canViewKeyResults: boolean;
  canAccessDrawer: boolean;
  // 2. Commenter permissions
  canViewComments: boolean;
  canComment: boolean;
  canAccessStatusTooltip: boolean;
  canViewAllResults: boolean;
  // 3. Editor permissions
  canEdit: boolean;
  canShare: boolean;
  // 4. Owner permissions
  canManageSettings: boolean;
}

/**
 * Pure utility function that derives permissions from a user's role
 *
 * @param role - The user's role on the map ('owner' | 'editor' | 'commenter' | 'viewer' | null)
 * @returns Object containing boolean permission flags
 *
 * @example
 * ```typescript
 * const permissions = getPermissionsForRole('editor');
 * if (permissions.canEdit) {
 *   // Show edit controls
 * }
 * ```
 *
 * Role hierarchy and permissions:
 * - **Owner**: Full permissions (can do everything)
 * - **Editor**: Can edit content + share + comment + view all
 * - **Commenter**: Can comment + view comments (no editing)
 * - **Viewer**: Can only view limited content (no comments, no editing)
 * - **null**: No access (all permissions false)
 */
export function getPermissionsForRole(role: Role | null): RolePermissions {
  const roles = ['unauthorized', 'viewer', 'commenter', 'editor', 'owner'] as const;

  const roleLevel = roles.indexOf(role ?? 'unauthorized');

  // 0. Unauthorized permissions. No access to the map.
  const permissions: RolePermissions = {
    canViewMap: false,
    canViewKeyResults: false,
    canAccessDrawer: false,
    canViewComments: false,
    canComment: false,
    canAccessStatusTooltip: false,
    canViewAllResults: false,
    canEdit: false,
    canShare: false,
    canManageSettings: false,
  };

  // 1. Viewer permissions
  if (roleLevel >= 1) {
    permissions.canViewKeyResults = true;
    permissions.canAccessDrawer = true;
    permissions.canViewMap = true;
  }

  // 2. Commenter permissions
  if (roleLevel >= 2) {
    permissions.canComment = true;
    permissions.canViewComments = true;
    permissions.canAccessStatusTooltip = true;
    permissions.canViewAllResults = true;
  }

  // 3. Editor permissions
  if (roleLevel >= 3) {
    permissions.canEdit = true;
    permissions.canShare = true;
  }

  // 4. Owner permissions
  if (roleLevel >= 4) {
    permissions.canManageSettings = true;
  }

  return permissions;
}
